import { apm } from '@elastic/apm-rum'
import * as Sentry from '@sentry/react'
import { appConfig } from '../config'
import { getStore } from '../redux/storeHolder'
import { buildErrorEvent } from './floodlightEvents'
import * as Enums from "./enums"
import * as Types from "./types"
import * as IBTypes from "ib/utils/types"
import Utils from "./utils"
import * as WeekendLeague from './weekendLeague'
import { readStargateAttribution } from './stargateAttribution' // THPRT-6415
import { MMAvalableFunds, MMDetails, PAMMCheckResponse, PAMMError, PAMMManagerOffer, PAMMWithradResponse, RegisterInvestorResponse, RegisterMMResponse, ReinvestResponse } from './pamm'


export enum Authorize { Yes, No }
type AuthResponse = Types.AuthResponse & Types.ErrorAuthResponse
type RequestParams = {
    payload: {
        module: string
        action: string
        parameters?: object
        page_request?: Types.PageRequest
    }[]
    session_id?: string
    token?: string
    challengeResponse?: string
    accessToken?: string
    refreshToken?: string
}
class Api {
    private tfboToken: string
    private tfboSession: string
    private accessToken: string
    private refreshToken: string
    private idToken: string
    private refreshTokenValidUntil: string
    private refreshPromise: Promise<boolean> | null = null;

    constructor() {
        this.tfboSession = localStorage[Enums.__STORAGE.session]
        this.tfboToken = localStorage[Enums.__STORAGE.token]
        this.accessToken = localStorage[Enums.__STORAGE.accessToken]
        this.refreshToken = localStorage[Enums.__STORAGE.refreshToken]
        this.idToken = localStorage[Enums.__STORAGE.idToken]
        this.refreshTokenValidUntil = localStorage[Enums.__STORAGE.validUntil]

    }
    async _request<T>(url: string,
        method: string,
        auth: Authorize,
        throwStatusError: boolean,
        data?: RequestParams | object,
        options: { contentType?: string; signal?: AbortSignal } = {}): Promise<T> {
        const { contentType = 'application/json', signal } = options
        const headers: HeadersInit = { 'Content-Type': contentType }

        if (auth === Authorize.Yes && this.accessToken) {
            headers.Authorization = `Bearer ${this.accessToken}`
        }

        const config: RequestInit = {
            method,
            headers,
            credentials: 'include',
            signal,
        }

        // logging into elastic apm
        let transaction: Transaction | undefined
        let fetchSpan: Span | undefined
        if (data) {
            config.body = JSON.stringify(data)
            const body = data as RequestParams
            if (body.payload) {
                const payloadNames = body.payload.map((x) => `${x.module} -> ${x.action}`)
                const name = payloadNames.length > 1 ? 'Multiple payloads' : payloadNames[0]
                const labelValue = payloadNames.join(', ')
                transaction = apm.startTransaction(name, 'custom.request')
                transaction?.addLabels({ payload: labelValue })
                fetchSpan = transaction?.startSpan(name, 'external.http')
            }
        }

        let statusErrorTracked = false
        try {
            const response = await fetch(url, config)
            fetchSpan?.end()

            if (throwStatusError && response.status !== 200) {
                statusErrorTracked = true
                this.trackApiError(url, method, data, 'status', String(response.status))
                throw Error(`Server is unavailable. Please try later. HTTP status: ${response.status}`)
            }

            const processSpan = transaction?.startSpan('Process response', 'external.process')
            const ct = response.headers.get("content-type")
            let result: unknown
            if (ct?.startsWith('application/json'))
                result = await response.json()
            else if (ct?.startsWith('application/octet-stream') || ct?.startsWith('image/png'))
                result = await response.blob()
            else
                result = response

            processSpan?.end()
            return result as T
        } catch (error) {
            // Ends the fetch span on failure too — including when the request is
            // aborted via AbortSignal (a DOMException named 'AbortError'), which
            // propagates to the caller without triggering the status-based
            // NOT_AUTHORIZED refresh path in _tfbo.
            fetchSpan?.end()
            // Genuine transport failure (fetch rejected / no response). Skip
            // aborts and the status error already tracked above.
            if (!statusErrorTracked && !this.isAbortError(error, signal)) {
                this.trackApiError(url, method, data, 'network', 'na')
            }
            throw error
        } finally {
            transaction?.end()
        }
    }

    private isAbortError(error: unknown, signal?: AbortSignal): boolean {
        if (signal?.aborted) return true
        return !!error && typeof error === 'object' && (error as { name?: string }).name === 'AbortError'
    }

    private deriveApiEndpoint(url: string): string {
        if (appConfig?.API_URL && url.startsWith(appConfig.API_URL)) return 'nsportal'
        if (appConfig?.AUTH_URL && url.startsWith(appConfig.AUTH_URL)) return 'auth'
        return 'na'
    }

    private deriveApiAction(url: string): string {
        try {
            const segments = new URL(url).pathname.split('/').filter(Boolean)
            return segments.length ? segments[segments.length - 1] : ''
        } catch {
            return ''
        }
    }

    private trackApiError(
        url: string,
        method: string,
        data: RequestParams | object | undefined,
        errorType: 'status' | 'network',
        errorCode: string
    ) {
        try {
            if (!window.dataLayer) return
            const app = getStore()?.getState?.()?.App
            const loggedIn = !!app?.loggedIn
            const language = app?.language ?? ''
            const userId = app?.userProfile?.id ?? null
            const accountId = app?.liveAccounts?.[0]?.account?.accountNumber ?? null
            const base = {
                errorType,
                errorCode,
                apiEndpoint: this.deriveApiEndpoint(url),
                apiMethod: method,
                loggedIn,
                language,
                pathname: window.location.pathname,
                userId,
                accountId,
            }
            const pairs = (data as RequestParams)?.payload
            if (Array.isArray(pairs) && pairs.length) {
                pairs.forEach((pair) =>
                    window.dataLayer?.push(buildErrorEvent({
                        ...base,
                        apiModule: pair?.module ?? '',
                        apiAction: pair?.action ?? '',
                    }))
                )
            } else {
                window.dataLayer?.push(buildErrorEvent({
                    ...base,
                    apiModule: '',
                    apiAction: this.deriveApiAction(url),
                }))
            }
        } catch {
            // Analytics tracking must never interfere with the real request.
        }
    }

    async _tfbo<T>(data: Partial<RequestParams> = {}, auth: Authorize = Authorize.Yes, signal?: AbortSignal) {
        if (auth === Authorize.Yes) {
            data.session_id = this.tfboSession
            data.token = this.tfboToken
        }
        const request = () => {
            return this._request<Types.APIResponse<T>>(appConfig.API_URL, 'post', auth, true, data, { signal })
        }

        const response = await request()
        const status = response.payload?.[0]?.status
        if (auth === Authorize.Yes && status === 'NOT_AUTHORIZED') {
            if (await this.refreshOnce()) {
                data.session_id = this.tfboSession
                data.token = this.tfboToken
                return request()
            }
            window.dispatchEvent(new Event("TokenExpired"))
        }
        return response
    }

    async _auth<T>(url: string, method: string, data?: object, auth: Authorize = Authorize.Yes) {
        const request = () => {
            return this._request<AuthResponse & T>(
                `${appConfig.AUTH_URL}/${url}`,
                method,
                auth,
                false,
                data)
        }

        const response = await request()
        if (auth === Authorize.Yes && response.code && response.status !== 'OK') {
            if (await this.refreshOnce())
                return request()
            window.dispatchEvent(new Event("TokenExpired"))
        }
        return response
    }

    _tfboCall<T>(module: string, action: string, parameters?: object, auth: Authorize = Authorize.Yes, signal?: AbortSignal) {
        return this._tfbo<T>({ payload: [{ module, action, parameters }] }, auth, signal)
    }

    private async _download<T>(url: string | (() => string)) {
        const getUrl = typeof url === 'function' ? url : () => url
        const doFetch = async (): Promise<Response> => {
            const headers: HeadersInit = { 'Content-Type': 'application/json' }
            if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`
            return fetch(getUrl(), { method: 'GET', headers, credentials: 'include' })
        }
        const response = await doFetch()
        if (response.status === 400 || response.status === 401) {
            if (await this.refreshOnce())
                return doFetch() as unknown as Response & T
            window.dispatchEvent(new Event("TokenExpired"))
        }
        return response as unknown as Response & T
    }

    downloadClientStatement = <T>(urlParams: string) =>
        this._download<T>(`${appConfig.CLIENT_STATEMENT_URL}/download?${urlParams}`)

    downloadTradeStatement = <T>(urlParams: string) =>
        this._download<T>(`${appConfig.TRADE_STATEMENT_CSV_URL}/download?${urlParams}`)

    downloadWithdrawalReceipt = <T>(urlParams: string) =>
        this._download<T>(`${appConfig.WITHDRAWAL_RECEIPT_URL}?${urlParams}`)

    /// Authentication ///

    registerUser = (data: Types.RegisterUserParams) => {
        const body: Types.AuthRegisterRequest = {
            email_id: data.email_id,
            password: data.password,
            first_name: data.first_name,
            last_name: data.last_name,
            country: data.country,
            account_holder_title: data.accountHolderTitle,
            preferred_language_code: data.preferred_language_code,
            utmLink: data.utmLink,
            brand: data.brand,
            source: data.source,
            visitorId: data.visitorId,
        }
        return this._auth<Types.AuthResponse>('auth/register', 'post', body, Authorize.No)
    };

    trackAttribution = (params: Types.AttributionTrackParams) => {
        this._tfboCall<{ success: boolean }>('attribution', 'track', params).catch(() => undefined)
    };

    Login = (data: Types.EmailAndPassword) => {
        const params = {
            email: data.email,
            password: data.password,
            recaptchaResponse: data.recaptchaResponse,
        }
        return this._auth<Types.ErrorAuthResponse>
            ('auth/login', 'post', params, Authorize.No)
    };

    socialRegister = async (data: {
        email_id: string
        first_name: string
        last_name: string
        country: number
        account_holder_title?: string
        utmLink?: string
        brand?: string
        source?: string
        visitorId?: string
    }) => {
        // Use ID token — Keycloak access tokens from Apple IDP may lack `sub`/`email`, causing auth-adapter 401
        const bearerToken = this.idToken || this.accessToken
        const response = await fetch(`${appConfig.AUTH_URL}/auth/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${bearerToken}`,
            },
            body: JSON.stringify(data),
        })
        if (!response.ok) {
            const body = await response.json().catch(() => ({}))
            throw new Error(body.description || body.error || `Registration failed (${response.status})`)
        }
        return response.json()
    };

    checkProfileStatus = async (): Promise<{ needsCompletion: boolean; missing?: string[] }> => {
        // Use ID token — Keycloak access tokens from Apple IDP may lack `sub`/`email`, causing auth-adapter 401
        const bearerToken = this.idToken || this.accessToken
        const response = await fetch(`${appConfig.AUTH_URL}/auth/profile/status`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${bearerToken}`,
            },
        })
        if (!response.ok) {
            throw new Error(`Profile status check failed (${response.status})`)
        }
        return response.json()
    };

    // set auth info
    setTFBOAuth = (session: string, token: string) => {
        this.tfboSession = session
        this.tfboToken = token
        localStorage[Enums.__STORAGE.session] = session
        localStorage[Enums.__STORAGE.token] = token
    };

    setAuthTokens = (tokens?: Partial<Types.AuthTokens>) => {
        if (tokens) {
            this.accessToken = tokens.accessToken ?? ''
            this.refreshToken = tokens.refreshToken ?? ''
            this.idToken = tokens.idToken ?? ''
            this.refreshTokenValidUntil = tokens.refreshTokenValidUntil ?? ''
            localStorage[Enums.__STORAGE.accessToken] = tokens.accessToken
            localStorage[Enums.__STORAGE.refreshToken] = tokens.refreshToken
            if (tokens.idToken) {
                localStorage[Enums.__STORAGE.idToken] = tokens.idToken
            }
            localStorage[Enums.__STORAGE.validUntil] = tokens.refreshTokenValidUntil
        }
        else {
            this.accessToken = ''
            this.refreshToken = ''
            this.idToken = ''
            this.refreshTokenValidUntil = ''
            localStorage.removeItem(Enums.__STORAGE.accessToken)
            localStorage.removeItem(Enums.__STORAGE.refreshToken)
            localStorage.removeItem(Enums.__STORAGE.idToken)
            localStorage.removeItem(Enums.__STORAGE.validUntil)
        }
    };

    // logout
    Logout = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- logout has no typed response
        return this._tfbo<any>({ payload: [{ module: 'authentication', action: 'logout' }] })
            .then((response) => {
                this.setTFBOAuth('', '')
                this.setAuthTokens(undefined)
                return response
            })
            .catch(() => {
                this.setTFBOAuth('', '')
                this.setAuthTokens(undefined)
            })
    };

    VerifyTokens = () => {
        if (this.accessToken && this.refreshToken) {
            return new Date(this.refreshTokenValidUntil).getTime() - Date.now() > 60_000
        }
        return false
    };

    refreshOnce = (): Promise<boolean> => {
        if (this.refreshPromise) return this.refreshPromise
        this.refreshPromise = this.UpdateTokens().finally(() => {
            this.refreshPromise = null
        })
        return this.refreshPromise
    }

    UpdateTokens = async (): Promise<boolean> => {
        if (!this.accessToken || !this.refreshToken) return false
        try {
            const response = await this._auth<{ status: string; tokens: Types.AuthTokens; code?: string }>(
                'auth/refresh',
                'post',
                { accessToken: this.accessToken, refreshToken: this.refreshToken },
                Authorize.No
            )
            if (response.status === 'OK' && response.tokens) {
                this.setAuthTokens(response.tokens)
                return true
            }
            return false
        } catch {
            return false
        }
    }

    // verify_password
    verifyPortalPassword = (data: Types.VerifyPasswordParams) =>
        this._tfboCall<void>("authentication", "verify_password", data)

    // change_password
    changePortalPassword = (data: Types.ChangePasswordParams) =>
        this._tfboCall<void>("authentication", "change_password", data)

    // forgot_password
    resetPasswordStepOne = (data: Pick<Types.ResetPassword, 'email_id' | 'response'>) =>
        this._tfboCall<Types.ReCaptchaResponse>("authentication", "forgot_password_web", data, Authorize.No)

    resetPasswordStepTwo = (data: Pick<Types.ResetPassword, 'password' | 'password_reset_token' | 'response'>) =>
        this._tfboCall<Types.ReCaptchaResponse>("authentication", "forgot_password_web", data, Authorize.No)

    //register

    /// Accounts ///
    // getUserAccounts
    getUserAccounts = (data: Types.getUserAccountsRequestParams) =>
        this._tfboCall<void>("account", "getUserAccounts", data)

    // canCreateAdditionalTradingAccount
    canCreateAdditionalTradingAccount = () =>
        this._tfboCall<boolean>("account", "canCreateAdditionalTradingAccount")

    // transfer
    transferMoneyStepOne = (data: Types.TransferMoneyStepOne) =>
        this._tfboCall<Types.TransferMoneyResponse>("account", "transfer", data)

    transferMoneyStepTwo = (data: Types.TransferMoneyStepTwo) =>
        this._tfboCall<Types.TransferMoneyResponse>("account", "transfer", data)

    availableInterAccountTransfer = () =>
        this._tfboCall<number>("account", "availableInterAccountTransfer", {})

    // getTransferHistory
    getTransferHistory = (pageRequest?: Types.PageRequest) =>
        this._tfboCall<Types.TransefrsHistoryResponse>("account", "getTransferHistory",
            pageRequest ? { page_request: pageRequest } : undefined)

    // verify
    verifyAccountPassword = (data: Types.VerifyAccountPasswordParams) =>
        this._tfboCall<void>("account", "verify", data)

    verifyTradingAccount = (data: Required<Pick<Types.UpdateTradingAccountParams, 'account' | 'password'>>) =>
        this._tfboCall<void>("account", "verify", data)

    // update
    updateTradingAccount = (data: Types.UpdateTradingAccountParams) =>
        this._tfboCall<void>("account", "update", data)

    getTradingAccountTempPassword = (accountNumber: number) =>
        this._tfboCall<Types.TradingAccountTempPasswordResponse>("account", "getAccountTempInfo", { accountNumber })

    // stat
    getAccountStat = (type?: Enums.TradeAccountType) =>
        this._tfboCall<Types.AccountStatResponse>("account", "stats",
            type ? { accountType: type === Enums.TradeAccountType.Live ? "LIVE" : "DEMO" } : {}
        )

    //add
    createTradingAccount = (data: Partial<Types.CreateTradingAccountParams>, challenge?: string) => {
        const params: RequestParams = {
            payload: [{
                module: "account",
                action: "add",
                parameters: {
                    live: data.type === Enums.TradeAccountType.Live,
                    currency: data.currency,
                    leverage: data.leverage,
                    balance: data.balance,
                    platformCode: data.platform,
                    platformAccountType: data.platformAccountType,
                    source: data.source,
                    accPassword: data.accPassword || undefined,
                    fromContest: data.fromContest,
                    username: data.username
                }
            }],
            ...(challenge && { challengeResponse: challenge }),
        }
        return this._tfbo<Types.CreateTradeAccountResponse>(params)
    }

    createDemoPortalAccount = (data: Partial<Types.CreateDemoPortalAccountParams>) =>
        this._tfboCall<Types.CreateTradeAccountResponse>("account", "adddemo", data, Authorize.No)

    registerDemo = async (data: Partial<Types.CreateDemoPortalAccountParams>, isSocial = false): Promise<Types.DemoRegistrationResponse> => {
        const headers: HeadersInit = { 'Content-Type': 'application/json' }
        if (isSocial) {
            const bearerToken = this.idToken || this.accessToken
            if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`
        }
        const response = await fetch(`${appConfig.AUTH_URL}/auth/register/demo`, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
        })
        const body = await response.json().catch(() => ({}))
        if (response.status === 409) {
            throw Object.assign(new Error('ACCOUNT_CREATION_ERR'), { status: 409 })
        }
        if (!response.ok) {
            throw new Error(body.description || `Demo registration failed (${response.status})`)
        }
        return body
    }

    // THPRT-6264 / TMJV-1001: register the demo user and establish a session
    // WITHOUT creating a trading account. Per-platform accounts are then added
    // via addDemoActivationAccount (account/add, live: false).
    //
    // Contract per docs/TMJV-1001-account-less-demo-registration.md: the session
    // token is returned in payload[0].result.token (login-style result, also
    // mirrored as the envelope session_id); pass it as session_id on subsequent
    // authenticated calls. No auth-service tokens are returned, so this session
    // is TFBO-only: a full page refresh mid-activation fails VerifyTokens in the
    // boot saga and logs the user out (accepted for v1, see THPRT-6264 PR notes).
    //
    // DUPLICATE EMAIL: per the guide, a password supplied for an already
    // registered email returns status ACCOUNT_CREATION_ERR; rethrown as
    // Error('ACCOUNT_CREATION_ERR') so Demo/index.tsx shows the modal.
    registerDemoUser = async (data: Partial<Types.RegisterDemoUserParams>, isSocial = false): Promise<Types.RegisterDemoUserResponse> => {
        if (appConfig.DEMO_ACTIVATION_MOCK) {
            const mock = await import('./demoActivationMock')
            const result = await mock.mockRegisterDemoUser(data)
            this.setTFBOAuth(result.token, result.token)
            return result
        }
        // Social sign-up is out of scope for registerdemo per the guide: the
        // account-less social flow is expected to reuse the existing SSO login
        // path, then account/add per platform. Confirmed open point with BE;
        // until then the bearer header is attached for the social variant.
        const response = await this._tfboCall<Types.RegisterDemoUserResponse>(
            "account", "registerdemo", data, isSocial ? Authorize.Yes : Authorize.No)
        try {
            this.checkTFBOResponse(response)
        } catch (err: unknown) {
            const tfboStatus = response.payload?.[0]?.status
            if (tfboStatus === 'ACCOUNT_CREATION_ERR' || tfboStatus === 'ALREADY_REGISTERED') {
                throw Object.assign(new Error('ACCOUNT_CREATION_ERR'), { status: 409 })
            }
            throw err
        }
        const result = response.payload?.[0]?.result
        // Verified empirically on UAT (THPRT-6264, 2026-07-08): registerdemo
        // upgrades the request's anonymous session, and the authenticated
        // credentials are the response ENVELOPE's session_id/token pair. The
        // integration guide's instruction to use result.token as the session_id
        // is wrong as deployed (result.token is rejected as NOT_AUTHORIZED in
        // every combination); discrepancy reported on TMJV-1001.
        const session = response.session_id
        const token = response.token
        if (!session || !token) {
            throw new Error('SESSION_MISSING')
        }
        this.setTFBOAuth(session, token)
        return result || ({ token: session } as Types.RegisterDemoUserResponse)
    }

    // THPRT-6264: thin wrapper -- delegates to createTradingAccount in prod or
    // the dev-only mock when DEMO_ACTIVATION_MOCK is true.
    addDemoActivationAccount = async (data: Partial<Types.CreateTradingAccountParams>, challenge?: string) => {
        if (appConfig.DEMO_ACTIVATION_MOCK) {
            const mock = await import('./demoActivationMock')
            return mock.mockAddDemoAccount(data)
        }
        return this.createTradingAccount(data, challenge)
    }

    // profile
    accountProfile = (accountId: number) =>
        this._tfboCall<void>("account", "profile", { accountId })

    /// Profile ///
    // get_popups
    getPopups = () =>
        this._tfboCall<void>("profile", "get_popups")

    // get_user
    getUserPayload = (): Types.RequestBody => ({ module: "profile", action: "get_user" })

    getUser = () =>
        this._tfboCall<Types.UserProfile>("profile", "get_user")

    addAttributeToUserProfile = (data: Partial<Types.UserProfile['additionalAttributes']>) =>
        this._tfboCall<void>("profile", "addAdditionalAttributeToUser", { additionalAttributes: data })

    // THPRT-6331: durable post-approval tax-id persistence. Writes User_Tax_Info
    // without touching application status (per BE, Valeri, profile/addNewTaxInfo).
    addNewTaxInfo = (country_id: number, tax_number: string) =>
        this._tfboCall<void>("profile", "addNewTaxInfo", { country_id, tax_number })

    // update
    updateUserProfile = (data: Types.UserProfile & { isADemoAccount?: boolean }) =>
        this._tfboCall<void>("profile", "update", data)

    // notifications
    getUserNotifications = (data?: Types.GetNotificationsParams) =>
        this._tfboCall<Types.Notification[]>("profile", "notifications", data)

    getNotificationsMap = () =>
        this._tfboCall<Types.Notification[]>("profile", "getnotification")

    clearNotifications = () =>
        this._tfboCall<void>("profile", "markReadNotification")
    // files
    getUserFiles = () =>
        this._tfboCall<Types.DocumentFile[]>("profile", "files")

    // claim
    claim = (data: Types.IndividualClaimParams) =>
        this._tfboCall<Types.IndexedObject | undefined>("profile", "claim", data)

    // claim documents. Difference from claim: doesnt check application, doesn't send emails
    claimDocuments = (data: Types.IndividualClaimParams) =>
        this._tfboCall<void>("profile", "claim_documents", data)

    // deleteCreditCard
    deleteCreditCard = (data: Types.DeleteCreditCardParams) =>
        this._tfboCall<void>("account", "deleteCreditCard", data)

    /// payment ///
    // transactions
    getTransactionsPayload = (data?: Types.GetTransactionsParams): Types.RequestBody => ({
        module: "payment",
        action: "transactions",
        parameters: data
    })

    getTransactions = (data?: Types.GetTransactionsParams) =>
        this._tfboCall<Types.Transaction[]>("payment", "transactions", data)

    // getProcessorsByAccount
    getProcessorsByAccount = (accountId: number) =>
        this._tfboCall<Types.AccountProcessor[]>("payment", "getProcessorsByAccount", {
            accountId,
            AllowCrypto: true,
        })

    getAvailablePraxisGateways = ({ locale, country }: { locale: string, country?: string }) =>
        this._tfboCall<Types.GateWaysResponse>("PraxisGateways", "getAvailableGateways", { locale, country })

    getPraxisSession = ({ locale, domain }: { locale: string, domain: string }) =>
        this._tfboCall<Types.PraxisSessionResponse>("PraxisGateways", "getPraxisSession", { locale, domain })

    // getProcessorsByCurrentUser
    getProcessorsByCurrentUser = () =>
        this._tfboCall<Types.AccountProcessor[]>("payment", "processors_by_current_user")

    // getBitGoAddress
    getBitGoAddress = (data: Types.GetBitGoAddressParams) =>
        this._tfboCall<Types.GetBitGoAddressResponse>("payment", "getBitGoAddress", data)
    // "parameters":{"userId":10089191,"cryptoCurrency":{"id": 181,"code": "BTC","name":"Bitcoin"}}}]}
    // initiatePayment
    initiatePayment = (data: Types.InitPaymentParams, challenge?: string) =>
        this._tfbo<Types.InitPaymentResponse>({
            payload: [{ module: "payment", action: "initiatePayment", parameters: data }],
            ...(challenge && { challengeResponse: challenge }),
        })

    // initiate
    initiate = (data: Types.InitiateParams, challenge?: string) =>
        this._tfbo<Types.InitiateResponse>({
            payload: [{ module: "payment", action: "initiate", parameters: data }],
            ...(challenge && { challengeResponse: challenge }),
        })

    appleWebPayVerification = (appleUrl: string) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped backend response
        this._tfboCall<any>("payment", "appleWebPayVerification", { appleUrl })

    // cardpayLATAMPaymentMethods
    cardpayLATAMPaymentMethods = () =>
        this._tfboCall<{ [key: string]: string }>("payment", "cardpayLATAMPaymentMethods", {})

    // generatePayPalToken
    generatePayPalToken = () =>
        this._tfboCall<string>("payment", "generatePayPalToken", {})

    // isPayPalSupported
    isPayPalSupported = (countryCode: string) =>
        this._tfboCall<boolean>("payment", "isPayPalSupported", { countryCode })

    // getusercreditcards
    getusercreditcards = (parameters: Types.GetUserCreditCardsParams) =>
        this._tfboCall<Types.CreditCard[]>("payment", "getusercreditcards", parameters)

    // getThunderXPayBanks
    getThunderXPayBanks = () =>
        this._tfboCall<Types.XPayQRBank[]>("payment", "getThunderXPayBanks", {})

    // getThunderXPayBanks
    fetchBankCodes = (isWithdrawal = false) =>
        this._tfboCall<Types.BJPBank[]>("payment", "fetchBankCodes", { isWithdrawal })

    // TMJV-871: now country-parameterised. The same endpoint serves Nigeria and
    // the LATAM countries; the BE returns the active banks for the given country.
    getPayretailersBanks = (countryId: number) =>
        this._tfboCall<Types.PayretailersBank[]>("PayretailersBankControllerCPortal", "getAllActivePayretailersBanks", { countryId })

    // TMJV-1176: VertuPay Vietnam bank list, DB-served per country for the
    // withdrawal Bank Name dropdown. Module = lowercased simple class name of
    // VertuPayBankCportalController; action = its @Point method.
    getVertuPayBanks = (countryId: number) =>
        this._tfboCall<Types.VertuPayBankDto[]>("VertuPayBankCportalController", "getVertuPayBanksByCountry", { countryId })

    subscribe = (parameters: Types.SubscriptionSubscribeParams) =>
        this._tfboCall<Types.SubscriptionSubscribeResponse>("subscription", "subscribe", parameters)

    editBankInfo = (parameters: Types.EditBankInfoParams) =>
        this._tfboCall<Types.SubscriptionSubscribeResponse>("subscription", "editBankInfo", parameters)

    getSubscriptionStatus = (parameters: Types.GetSubscriptionStatusParams) =>
        this._tfboCall<Types.GetSubscriptionStatusResponse>("subscription", "getSubscriptionStatus", parameters)

    changeTier = (parameters: Types.ChangeTierParams) =>
        this._tfboCall<Types.ChangeTierResponse>("subscription", "changeTier", parameters)

    stopSubscription = (parameters: Types.StopSubscriptionParams) =>
        this._tfboCall<Types.StopSubscriptionResponse>("subscription", "stopSubscription", parameters)

    getPaymentDetails = (parameters: Types.GetPaymentDetailsParams) =>
        this._tfboCall<Types.GetPaymentDetailsResponse>("subscription", "getPaymentDetails", parameters)

    getBillingHistory = (parameters: Types.GetBillingHistoryParams) =>
        this._tfboCall<Types.SubscriptionBillingHistoryItem[]>("subscription", "getBillingHistory", parameters)

    checkOpenPositionsTT = (parameters: Types.CheckOpenPositionsTTParams) =>
        this._tfboCall<Types.CheckOpenPositionsTTResponse>("subscription", "checkOpenPositionsTT", parameters)

    restartSubscription = (parameters: Types.RestartSubscriptionParams) =>
        this._tfboCall<Types.RestartSubscriptionResponse>("subscription", "restartSubscription", parameters)

    cancelSubscription = (parameters: Types.CancelSubscriptionParams) =>
        this._tfboCall<Types.CancelSubscriptionResponse>("subscription", "cancelSubscription", parameters)

    // includeTotals asks the backend to compute freezeMoney + pendingWithdrawalSum in the
    // same request (TMJV-628), letting the withdrawal screen drop the separate
    // getFreezeMoney / getpendingwithdrawalsum round-trips. Sent as the string "true"
    // (matched case-insensitively server-side).
    getWithdrawalOptions = (accountId: number, includeTotals?: boolean, signal?: AbortSignal) =>
        this._tfboCall<Types.WithdrawalOptions>("payment", "getwithdrawaloptions", includeTotals ? { accountId, includeTotals: "true" } : { accountId }, Authorize.Yes, signal)

    getPendingWithdrawalSum = (accountId: number, signal?: AbortSignal) =>
        this._tfboCall<number>("account", "getpendingwithdrawalsum", { accountId }, Authorize.Yes, signal)

    getFreezeMoney = (accountId: number, signal?: AbortSignal) =>
        this._tfboCall<number>("payment", "getFreezeMoney", { accountId }, Authorize.Yes, signal)

    // add_bank_account
    addBankAccount = (data: Types.AddBankAccountParams) =>
        this._tfboCall<Record<string, never>>("payment", "add_bank_account", data)

    // delete_bank_account
    deleteBankAccount = (data: Types.BankAccount) =>
        this._tfboCall<Record<string, never>>("payment", "delete_bank_account", { accountNumber: data.accountNumber })

    // get_bank_accounts
    getBankAccounts = () =>
        this._tfboCall<Types.BankAccount[]>("payment", "get_bank_accounts")

    // get_payment_info
    // getUserCreditCards
    getPaymentInfo = (data: Types.getPaymentInfoParams) =>
        this._tfboCall<Types.Transaction>("payment", "get_payment_info", data)

    getUserCreditCards = (data: Types.GetUserCreditCardsRequestParams) =>
        this._tfboCall<Types.CreditCard[]>("payment", "getUserCreditCards", data)

    submitWithdrawal = (data: Types.SubmitWithdrawalParams) =>
        this._tfboCall<Types.Transaction[]>("payment", "submitWithdrawal", data)

    cancelwithdraw = (transactionID: number) =>
        this._tfboCall<void>("payment", "cancelwithdraw", { id: transactionID })

    sendPhoneVerificationCode = () =>
        this._tfboCall<Types.TwilioSmsReponse>("phoneVerification", "sendVerificationCode", {})

    checkPhoneVerificationCode = (code: string, sid: string) =>
        this._tfboCall<Types.CheckVerificationCodeResponse>("phoneVerification", "checkVerificationCode", { code, sid })

    sendPaymentVerificationCode = (data: Types.SendTwilioSmsParams, channel: Types.OtpChannel) =>
        this._tfboCall<Types.TwilioSmsReponse>("payment", "sendVerificationCode", { ...data, channel })

    checkPaymentVerificationCode = (data: Types.CheckTwilioSmsParams) =>
        this._tfboCall<Types.TwilioSmsReponse>("payment", "checkVerificationCode", data)

    // getAstroPayDirectBanks
    // getPaymentsCount

    /// utility ///
    // getOrganizationSettings
    getOrganizationSettings = (country_id?: number) =>
        this._tfboCall<Types.OrganizationSettings>("utility", "getorganizationsettings", {
            type: "LIVE",
            ...(country_id && { country_id }),
        })
    getQuestions = (orgId: number) =>
        this._tfboCall<Types.Question[]>("application", "getQuestions", { orgId }, Authorize.No)

    getTrustDockVerificationID = (applicationID: number) =>
        this._tfboCall<Types.TrustDockResponse>("application", "get_trust_dock_verification_id", { applicationId: applicationID })

    getSumSubAccessToken = (applicationID: number) =>
        this._tfboCall<string>("application", "get_sum_sub_app_token", { applicationId: applicationID })

    getLastApplicationsInfo = () =>
        this._tfboCall<Types.AppInfo[]>("application", "getLastApplicationsInfo", {})

    getDocumentTypesPayload = (): Types.RequestBody => ({ module: "utility", action: "getdocumenttypes" })

    getDocumentTypes = (signal?: AbortSignal) =>
        this._tfboCall<Types.DocType[]>("utility", "getdocumenttypes", undefined, Authorize.No, signal)

    getTMCYOrgOverrides = () =>
        this._tfboCall<string[]>("utility", "getTMCYOrgOverrides", undefined, Authorize.No)



    // getCountries
    getCountriesPayload = (showUnused: boolean): Types.RequestBody => ({
        module: "utility", action: "getCountries", parameters: { showUnused }
    })

    getCountries = (showUnused = false, signal?: AbortSignal) =>
        this._tfboCall<Types.Country[]>("utility", "getCountries", { showUnused }, Authorize.No, signal)

    getcurrenciesPayload = (processorId?: number): Types.RequestBody => ({
        module: "utility", action: "getcurrencies", parameters: { processorId }
    })

    getcurrencies = (processorId?: number) =>
        this._tfboCall<Types.Currency[]>("utility", "getcurrencies", { processorId }, Authorize.No)

    getKYCProvider = (countryId: number) =>
        this._tfboCall<Types.KYCProvider>("utility", "getkycprovider", { countryId }, Authorize.No)

    checkUserTermsAndConditionsPayload = (): Types.RequestBody => ({ module: "profile", action: "checkUserTermsAndConditions" })

    checkUserTermsAndConditions = () =>
        this._tfboCall<Types.UserTermsAndConditions>("profile", "checkUserTermsAndConditions")

    getLatestTermsAndConditionsPayload = (): Types.RequestBody => ({ module: "profile", action: "getLatestTermsAndConditions" })

    getLatestTermsAndConditions = () =>
        this._tfboCall<Types.LatestTermsAndConditions>("profile", "getLatestTermsAndConditions")

    agreeToLatestTermsAndConditions = (versionNumber: string) =>
        this._tfboCall<void>("profile", "agreeToLatestTermsAndConditions", { versionNumber })

    // getAvatars
    getAvatars = () =>
        this._tfboCall<Types.IAvatarImage[]>("profile", "getAvatars", {})

    // changeAvatar
    changeAvatar = (attachmentId: number) =>
        this._tfboCall<Record<string, never>>("profile", "changeAvatar", { attachmentId })

    // isAutoWithdrawFormEnabled
    // ip
    getIPPayload = (): Types.RequestBody => ({ module: "utility", action: "ip" })

    getIP = () =>
        this._tfboCall<Types.GetIPResponse>("utility", "ip", undefined, Authorize.No)

    // getPreferredLanguages
    getPreferredLanguagesPayload = (): Types.RequestBody => ({ module: "utility", action: "getpreferredlanguages" })

    getPreferredLanguages = () =>
        this._tfboCall<Types.PreferredLanguage>("utility", "getpreferredlanguages", undefined, Authorize.No)

    getTradingCentralLink = (data: Types.GetTradingCentralLinkParams) =>
        this._tfboCall<string>("utility", "createHandshakeURL", data)

    getResearchPlatformLink = (data: Types.GetTradingCentralLoginLinkParams) =>
        this._tfboCall<string>("utility", "createTCLoginURL", data)

    /// application ///
    // apps
    getUserAppsPayload = (): Types.RequestBody => ({ module: "application", action: "apps" })

    getUserApps = () =>
        this._tfboCall<Types.UserApplication>("application", "apps")

    checkApplicationStatusesPayload = (): Types.RequestBody => ({ module: "application", action: "check_application_statuses" })

    checkApplicationStatuses = () =>
        this._tfboCall<Types.ApplicationStatusResponse[]>("application", "check_application_statuses")

    checkApplicationStatusByIDPayload = (applicationId: number): Types.RequestBody => ({
        module: "application",
        action: "check_application_status_by_id",
        parameters: { applicationId }
    })

    checkApplicationStatusByID = (applicationId: number) =>
        this._tfboCall<Types.ApplicationStatusResponse>("application", "check_application_status_by_id", { applicationId })

    // appExists = (accountHolderEmail: string) => {
    //     const params = this._createParams(
    //         {
    //             module: "application",
    //             action: "check_applicant_existance",
    //             parameters: { accountHolderEmail }
    //         }
    //     )

    //     return this._post<Types.AppExistsResponse>(Authorize.Yes,params)
    // }

    validateEmail = (data: Types.ValidateEmailParams) =>
        this._tfboCall<void>("application", "validateemail", data, Authorize.No)

    // THPRT-6415: merge the captured Stargate attribution (token forwarded verbatim
    // as `sgClick`, sub-IDs as `subIds`) into an application-submit parameters object,
    // in the same object that already carries utmLink/formStep; ref and utm_* are not
    // needed. Additive: returns `data` unchanged when there is no fresh captured token
    // (readStargateAttribution() is null past the 30-day TTL, so expired tokens are not
    // sent). Applied to every submit that mints or advances an application so a referred
    // registration is attributed under both standard and simplified onboarding; the
    // backend (TMJV-1157) reads it on the submit path and dedupes on the application id.
    withStargateAttribution = (data: Types.AppInfo): object => {
        const sg = readStargateAttribution()
        return sg ? { ...data, sgClick: sg.token, ...(sg.subIds ? { subIds: sg.subIds } : {}) } : data
    }

    incrementalSubmit = (data: Types.AppInfo) => {
        const authorized = this.tfboSession || this.accessToken
        return this._tfboCall<Types.IncrementalSubmitResponse>(
            "application",
            authorized ? "application_submit" : "incremental_submit",
            this.withStargateAttribution(data),
            authorized ? Authorize.Yes : Authorize.No
        )
    }

    simplified_submit_level_one = (data: Types.AppInfo) =>
        this._tfboCall<Types.SimplifiedSubmitLevelOneResponse>("application", "simplified_submit_level_one", this.withStargateAttribution(data))

    simplified_submit_level_two = (data: Types.AppInfo) =>
        this._tfboCall<Types.SimplifiedSubmitLevelOneResponse>("application", "simplified_submit_level_two", this.withStargateAttribution(data))

    getAccountTradingTypes = () =>
        this._tfboCall<Types.AccountTradingTypeModel[]>("utility", "getAccountTradingTypes", {}, Authorize.No)

    getJumioUrl = (type: Types.JumioAcceptedDocument, locale: string) =>
        this._tfboCall<Types.JumioUrlResponse>("application", "jumio_iframe_redirect_url", { type, locale })

    getJumioDocumentTypes = () =>
        this._tfboCall<Types.JumioDocumentTypesResponse>("application", "getJumioDocumentTypes")

    sendOtpCode = (data: Types.VerifyEmailParams) =>
        this._tfboCall<boolean>("emailvalidation", "send_verification_code", data, Authorize.No)

    verifyOtpCode = (otpCode: string, email: string) =>
        this._tfboCall<boolean>("emailvalidation", "verify_otp_code", {
            otpValue: otpCode,
            accountHolderEmail: email
        }, Authorize.No)

    isValidOtpCode = (otpCode: string, recaptchaResponse: string) =>
        this._tfboCall<Types.IsValidOtpCodeResponse>("utility", "isValidOtpCode", {
            otpCode,
            recaptchaResponse,
        })

    isUserVerifiedPayload = (email: string): Types.RequestBody => ({
        module: "emailvalidation",
        action: "isuserverified",
        parameters: { userEmail: email }
    })

    isUserVerified = (email: string) =>
        this._tfboCall<boolean>("emailvalidation", "isuserverified", { userEmail: email }, Authorize.No)


    isEmailVerificationRequired = (countryId: number) =>
        this._tfboCall<boolean>("emailvalidation", "isemail_verification_required", { originCountry: countryId }, Authorize.No)

    //returns AddressLookupResponse
    addressLookup = (data: Types.AddressLookupParams) =>
        this._tfboCall<Types.AddressLookupRecord[]>("application", "addressLookup", data, Authorize.No)

    getDocumentsJPN = () =>
        this._tfboCall<Types.DocsJPNResponse>("application", "get_documents", {})

    getMigrationStatus = () =>
        this._tfboCall<Types.MigrationInfo>("migration", "getMigrationStatus", {})

    setMigrationResponse = (response: Types.UserMigrationResponse) =>
        this._tfboCall<void>("migration", "setMigrationResponse", { response })

    appInfoPayload = (): Types.RequestBody => ({
        module: "application",
        action: "get_application_info",
        parameters: { email: Utils.loadEncryptedString(Enums.__STORAGE.user) }
    })

    appInfo = () =>
        this._tfboCall<Types.AppInfo>("application", "get_application_info", {
            email: Utils.loadEncryptedString(Enums.__STORAGE.user)
        }, Authorize.No)

    sendLastField = (data: { formLastField: string, formLastFieldCompleted?: string }) =>
        this._tfboCall<void>("application", "setFormLastField", {
            accountHolderEmail: Utils.loadEncryptedString(Enums.__STORAGE.user),
            formLastField: data.formLastField,
            ...(data.formLastFieldCompleted !== undefined && { formLastFieldCompleted: data.formLastFieldCompleted })
        }, Authorize.No)

    getAppRules = (country_id: number, accountType?: Types.ApplicationType) =>
        this._tfboCall<Types.AccountValidationRules>("application", "get_validation_rules", { country_id, accountType }, Authorize.No)

    //utility
    multilpeTFBO = (data: Types.RequestBody[], auth: Authorize = Authorize.Yes) => {
        const request = { payload: data }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- batch response; callers cast per-item
        return this._tfbo<any>(request, auth)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- callers cast response type
    checkTFBOResponse = (response: Types.APIResponse<any>,
        alsoValid?: Types.ResponseStatus[]): Types.ResponseStatus => {
        const payload = response.payload?.[0]
        const status = payload?.status
        if (!status) throw Utils.apiError(response)
        const valid = alsoValid ? ['OK', ...alsoValid] : ['OK']
        if (!valid.includes(status.toUpperCase())) {
            if (status === 'SYS_ERR') {
                Sentry.captureException(
                    new Error(`TFBO SYS_ERR: ${payload.module}->${payload.action}: ${payload.result?.message ?? ''}`),
                    {
                        tags: { module: payload.module, action: payload.action, status },
                    }
                )
            }
            throw Utils.apiError(response)
        }
        return status
    }

    //2fa

    FA2Options = (): RequestInit => {
        return {
            // mode: 'no-cors',
            headers: {
                'Content-Type': 'application/json',
                'session': `Bearer ${this.accessToken}`,
                'token': `Bearer ${this.accessToken}`
            }
        }
    }


    get2FaStatus = () =>
        this._auth<Types.FA2Status>('user/tfa/status', 'get')

    set2FaStatus = (enable: boolean, code: string) =>
        this._auth<Types.ErrorAuthResponse>(
            enable ? 'user/tfa/enable' : 'user/tfa/disable',
            'post', { code }
        )

    verify2FaCode = (data: Types.FA2VerifyRequest) =>
        this._auth<Types.ErrorAuthResponse>('auth/tfa', 'post', data)

    verifyTotpCode = (code: string) =>
        this._tfboCall<Record<string, never>>("authentication", "verifyTotpCode", { code })

    generate2FaCode = () =>
        this._auth<Types.FA2QRCode>('user/tfa/generate', 'post')

    // Shared helper: fetch a URL and parse the JSON response, or throw on non-200.
    private _fetchJson = async <T>(url: string, init: RequestInit = {}): Promise<T> => {
        const response = await fetch(url, init)
        if (response.status !== 200) throw new Error(response.statusText)
        return response.json()
    }

    getCurrencyPairRate = (params: Types.CurrencyPairRateParams) =>
        this._fetchJson<Types.CurrencyPairRateResponse>(
            `${appConfig.EXCHANGE_RATES_URL}/currency-pair?amount=${params.amount}&from=${params.from}&to=${params.to}&direction=${params.direction}`
        )

    addressLookupAutocomplete = (params: Types.AddressLookupAutocompleteParams) =>
        this._fetchJson<Types.AddressLookupAutocompleteResponse>(
            `${appConfig.ADDRESS_LOOKUP_URL}/lookup`,
            { headers: { 'Content-Type': 'application/json' }, method: 'POST', body: JSON.stringify(params) }
        )

    addressLookupComponents = (params: Types.AddressLookupComponentsParams) =>
        this._fetchJson<Types.AddressLookupComponentsResponse>(
            `${appConfig.ADDRESS_LOOKUP_URL}/components`,
            { headers: { 'Content-Type': 'application/json' }, method: 'POST', body: JSON.stringify(params) }
        )

    bjpPaymentFinishCallback = (transaction_id: string, status: string) =>
        this._fetchJson(
            `${appConfig.PAYMENT_URL}/finish_callback/Bjp?transaction_id=${transaction_id}&status=${status}`,
            { ...this.FA2Options(), method: 'POST' }
        )

    generateSsoToken = () => {
        return this._auth<{
            ssoCode: string
        } & Types.ErrorAuthResponse>('internal/sso', 'post', { refreshToken: this.refreshToken })
    }

    validateSsoToken = async (parameters: Types.ValidateSsoTokenParams): Promise<boolean> => {
        try {
            const response = await this._auth<Types.AuthTokens>(
                'internal/sso/exchange', 'post', parameters, Authorize.No)
            if (response.accessToken) {
                this.setAuthTokens(response)
                return true
            }
            return false
        } catch (e) {
            return false
        }
    }

    getalltrades = (parameters: Types.GetAllAccountTradesParams) =>
        this._tfboCall<Types.AccountTrade[]>("account", "getalltrades", parameters)

    sendTradeStatementsEmail = (parameters: Types.GetAllAccountTradesParams) =>
        this._tfboCall<void>("account", "sendTradeStatementsEmail", parameters)

    getAccountDetails = (tradingId: number) =>
        this._tfboCall<Types.AccountDetails>("account", "getAccountDetails", { tradingId })

    getGreenIDUIShow = (applicationID: number) =>
        this._tfboCall<Types.GreenID>("application", "greenIDUIShow", { applicationId: applicationID })


    getGreenIDUISubmitCallBack = (applicationID: number) =>
        this._tfboCall<void>("application", "greenIDUISubmitCallback", { applicationId: applicationID })

    isThinkInvestOptionDisabled = (affiliateId: string, userId: number) =>
        this._tfboCall<boolean>("account", "isThinkInvestOptionDisabled", { affiliateId, userId })

    getGreenIDUIAbortCallback = (applicationID: number) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped backend response
        this._tfboCall<any>("application", "greenIDUIAbortCallback", { applicationId: applicationID })

    refreshTTDemoBalance = (accountId: number, amount: number) =>
        this._tfboCall<Record<string, never>>("account", "refreshTTDemoBalance", { accountId, amount })

    addTrustedDevice = () =>
        this._tfboCall<Record<string, never>>("authentication", "addTrustedDevice", {})

    getTrustedDeviceStatus = () =>
        this._tfboCall<boolean>("authentication", "gettrusteddevicestatus", {})

    listTrustedDevice = () =>
        this._tfboCall<Types.TrustedDeviceListResponse>("authentication", "listTrustedDevices", {})

    removeTrustedDevice = (deviceId?: number) =>
        this._tfboCall<Record<string, never>>("authentication", "removeTrustedDevice", { deviceId })
    sendPartnershipQuestionnaire = (details: {
        user: number
        partnershipType: string// 'IB' | 'MM'
        answers: Array<{
            question: string
            answer?: string
        }>
    }) =>
        this._tfboCall<Record<string, never>>("partnership", "sendPartnershipQuestionnaire", {
            userId: details.user,
            partnershipType: details.partnershipType,
            filledQuestionnaire: details.answers
        })

    validateIB = (id: number) =>
        this._tfboCall<Record<string, never>>("application", "does_affiliate_exist_by_aid", { aid: id }, Authorize.No)

    findAllAvailableRequestTypes = () =>
        this._tfboCall<Types.SupportRequestType[]>("supportrequest", "findAllAvailableRequestTypes", {})

    findAllRequestTypes = () =>
        this._tfboCall<Types.SupportRequestType[]>("supportrequest", "findAllRequestTypes", {})

    saveSupportRequest = (parameters: Types.SaveSupportRequest) =>
        this._tfboCall<Record<string, never>>("supportrequest", "savesupportrequest", parameters)

    getAllSupportRequests = (parameters: Types.GetAllSupportRequests) =>
        this._tfboCall<Types.GetAllSupportRequestsResponse>("supportrequest", "searchByFilter", parameters)

    rateSupportRequest = (parameters: Types.RateSupportRequestParameters) =>
        this._tfboCall<Record<string, never>>("supportrequest", "rateSupportRequest", parameters)

    getAccountsByRequestType = (requestTypeId: number) =>
        this._tfboCall<Types.AccountStatResponse>("supportrequest", "getAccountsByRequestType", { requestTypeId })

    getLastDemoContestDate = () =>
        this._tfboCall<string>("account", "getLastDemoContestDate", {})

    getUserTradingVolume = (currency?: string, signal?: AbortSignal) =>
        this._tfboCall<Types.GetUserTradingVolumeResponse>("payment", "getUserTradingVolume", { currency }, Authorize.Yes, signal)

    checkPercentageAndRatioConfiguration = (subProcessorId: number) =>
        this._tfboCall<boolean>("payment", "checkPercentageAndRatioConfiguration", { subProcessorId })

    checkUserReferrerStatus = (userId: number) =>
        this._tfboCall<Types.UserReferrerStatus>("referafriend", "checkUserReferrerStatus", { userId })

    checkQualificationCriteriaForTheReferrer = () =>
        this._tfboCall<Types.QualificationForTheReferrer>("referafriend", "checkQualificationCriteriaForTheReferrer", {})

    addNewReferrer = (userId: number) =>
        this._tfboCall<Types.AddNewReferrerResponse>("referafriend", "addNewReferrer", { userId })

    generateRefLinkPayload = (): Types.RequestBody => ({ module: "referafriend", action: "generateRefLink", parameters: {} })

    generateRefLink = () =>
        this._tfboCall<Types.GenerateRefLinkResponse>("referafriend", "generateRefLink", {})

    getReferralActivitiesPayload = (userId: number): Types.RequestBody => ({
        module: "referafriend", action: "getReferralActivities", parameters: { userId }
    })

    getReferralActivities = (userId: number) =>
        this._tfboCall<Types.ReferralActivity[]>("referafriend", "getReferralActivities", { userId })

    getRemainigDeposit = (accountId: number) =>
        this._tfboCall<Types.RemainingDeposit>("account", "getRemainingDepositAmount", { accountId })

    // Partner Portal

    getpartnerinfoPayload = (parameters: Types.GetWithUserIDParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getpartnerinfo", parameters
    })

    searchPartnerClientListPayload = (parameters: Types.SearchPartnerClientListParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "searchPartnerClientList", parameters
    })

    searchpartnerusersummaryPayload = (parameters: Types.SearchPartnerUserSummaryParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "searchpartnerusersummary", parameters
    })

    getIBCountriesPayload = (): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getCountries", parameters: {}
    })

    getIBTradingPlatformsPayload = (): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getTradingPlatforms", parameters: {}
    })

    getIBAccountTradingTypesPayload = (): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getAccountTradingTypes", parameters: {}
    })

    searchClientAllAccountsPayload = (parameters: Types.SearchClientAllAccountsParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "searchClientAllAccounts", parameters
    })

    searchClientAllAccounts = (parameters: Types.SearchClientAllAccountsParams) =>
        this._tfboCall<Types.SearchClientAllAccountsResponse>("ibcommissioncportalmanagement", "searchClientAllAccounts", parameters)

    searchClientSummaryPayload = (parameters: Types.SearchClientSummaryParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "searchClientSummary", parameters
    })

    searchClientSummary = (parameters: Types.SearchClientSummaryParams) =>
        this._tfboCall<Types.ClientSummaryResponse>("ibcommissioncportalmanagement", "searchClientSummary", parameters)

    searchClientTransactionSummaryPayload = (parameters: Types.SearchClientTransactionsParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "searchClientTransactionSummary", parameters
    })

    searchClientTransactionSummary = (parameters: Types.SearchClientTransactionsParams) =>
        this._tfboCall<Types.SearchClientTransactionsResponse>("ibcommissioncportalmanagement", "searchClientTransactionSummary", parameters)

    getCashbackType = () =>
        this._tfboCall<Types.CashBackTypeObject>("ibcommissioncportalmanagement", "getcashbacktype", {})

    updateCashback = (parameters: Types.UpdateCashbackParams) =>
        this._tfboCall<Record<string, never>>("ibcommissioncportalmanagement", "updateCashback", parameters)

    getPartnerCountrySettings = () =>
        this._tfboCall<IBTypes.PartnerCountrySettings[]>("ibcommissioncportalmanagement", "getPartnerCountrySettings", {}, Authorize.No)

    isVietnamCalculationModel = (partnerId: number) =>
        this._tfboCall<boolean>("ibcommissioncportalmanagement", "isVietnamCalculationModel", { partnerId })

    isVietnamMarkupIb = (partnerId: number) =>
        this._tfboCall<boolean>("ibcommissioncportalmanagement", "isvietnammarkupib", { partnerId })

    getVietnamSubIbsExport = (parameters: Types.GetSubLevelIbsParams) =>
        this._tfboCall<Types.VTsubibsExportItem[]>("ibcommissioncportalmanagement", "getVietnamSubIbsExport", parameters)


    getsublevelibs = (parameters: Types.GetSubLevelIbsParams) =>
        this._tfboCall<Types.SubLevelIB[]>("ibcommissioncportalmanagement", "getsublevelibs", parameters)

    createIBApplication = (data: Partial<IBTypes.IBApplicationInit>) =>
        this._tfboCall<void>("UserPartnerApplicationController", "submitUserPartnerApplication", data)

    setIBApplicationStep = (applicationId: number, step: IBTypes.IBApplicationStep) =>
        this._tfboCall<void>("UserPartnerApplicationController", "setApplicationFormStep", {
            applicationId,
            formStep: step
        })

    completeIBApplication = (userId: number) =>
        this._tfboCall<IBTypes.IBAccountCreatedResponse>("UserPartnerApplicationController", "processApplicationAndCreateAccount", {
            userId,
            termsAndConditionConsent: "accepted"
        })

    createDefaultPartner = (accountNumber: number, applicationId: number) =>
        this._tfboCall<IBTypes.DefaultPartnerResponse>("PartnerController", "createDefaultPartner", { accountNumber, applicationId })

    createPartnerLink = (data: {
        campId: number
        partnerId: number
        redirecturl: string
    }) =>
        this._tfboCall<string>("PartnerTrackingLinkController", "generateTrackingLink", data)

    createRetailPartnerLink = (data: {
        cid: number
        pid: number
    }) =>
        this._tfboCall<string>("PartnerTrackingLinkController", "generateRetailTrackingLink", data)

    getIBApplicationPayload = (): Types.RequestBody => ({
        module: "UserPartnerApplicationController", action: "checkForApplication", parameters: {}
    })

    getIBApplication = () =>
        this._tfboCall<IBTypes.IBApplication>("UserPartnerApplicationController", "checkForApplication", {})

    updateIBApplicationStatus = (status: 'pendingKYC' | 'pendingReviw', applicationId: number) =>
        this._tfboCall<void>("UserPartnerApplicationController",
            status === 'pendingKYC' ? "updateStatusPendingKYC" : "updateStatusPendingReview",
            { applicationId })

    getAllIBQuestions = () =>
        this._tfboCall<Types.Question[]>("PartnerApplicationQuestionnaireController", "getAllPartnerQuestions", {})

    getIBQuestions = (orgId?: number) =>
        this._tfboCall<Types.Question[]>("PartnerApplicationQuestionnaireController", "getPartnerQuestions", { orgId })

    postIBAnswers = ({ organizationId, applicationId, answers }
        : {
            organizationId: number
            applicationId: number
            answers: Types.Question[]
        }) =>
        this._tfboCall<Types.Question[]>("PartnerApplicationQuestionnaireController", "savePartnerQuestionAnswersDetails", {
            organizationId,
            applicationId,
            result: answers
        })

    postIBAddress = (data: IBTypes.IBAddress) =>
        this._tfboCall<void>("userpartneraddresscontroller", "registeruseraddress", data)

    postIBPhone = (data: IBTypes.IBContactPhone) =>
        this._tfboCall<void>("UserPartnerAddressController", "registerOrUpdateUserPhone", data)

    claimIBDocument = (data: Types.IBClaimParams) =>
        this._tfboCall<void>("PartnerApplicationDocumentController", "submitPartnerDocuments", data)

    getIBFiles = (data: Partial<{
        userId: number
        applicationId: number
    }>) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped backend response
        this._tfboCall<any>("PartnerApplicationDocumentController", "findUserDocuments", data)

    getIBAnswers = (applicationId?: number) =>
        this._tfboCall<Types.Question[]>("PartnerApplicationQuestionnaireController", "getpartnerquestionanswerdetails", { applicationId })
    getAvailableInvoicesPayload = (parameters: Types.GetWithUserIDParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getAvailableInvoices", parameters
    })

    getInvoicePayoutHistoryPayload = (parameters: Types.GetWithUserIDParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getInvoicePayoutHistory", parameters
    })

    getAvailableForWithdrawalPayload = (parameters: Types.GetWithUserIDParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getAvailableForWithdrawal", parameters
    })

    getAwaitingPaymentPayload = (parameters: Types.GetWithUserIDParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getAwaitingPayment", parameters
    })

    getTotalCommissionsPayload = (parameters: Types.GetWithUserIDParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getTotalCommissions", parameters
    })

    getCashbackPayload = (parameters: Types.GetWithUserIDParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getCashback", parameters
    })

    requestInvoicesPayment = (parameters: Types.RequestInvoicesPaymentParams) =>
        this._tfboCall<Record<string, never>>("ibcommissioncportalmanagement", "requestInvoicesPayment", parameters)

    getUserPartnerApplication = (parameters: Types.GetWithUserIDParams) =>
        this._tfboCall<IBTypes.UserPartnerApplication>("ibcommissioncportalmanagement", "getUserPartnerApplication", parameters)

    getGBGScanTokens = () =>
        this._tfboCall<Types.GBGScanTokensResponse>("IDScanController", "token", { token_scope: "scanning" })

    getRemainingDepositAmount = (accountId: number) =>
        this._tfboCall<Types.RemainingDepositAmountResponse>("account", "getRemainingDepositAmount", { accountId })

    getPerformanceSummary = (parameters: Types.GetPerformanceSummaryParams) =>
        this._tfboCall<IBTypes.PerformanceReport>("ibcommissioncportalmanagement", "getPerformanceSummary", parameters)

    searchPartnerAllTradingAccounts = (parameters: Types.SearchPartnerAllTradingAccountsParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "searchPartnerAllTradingAccounts", parameters
    })

    getTraderTransactionReport = (parameters: IBTypes.GetTraderTransactionReportParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "getTraderTransactionReport", parameters
    })

    getMyPortfolioAccountReport = (parameters: IBTypes.GetMyPortfolioAccountReportParams) =>
        this._tfboCall<IBTypes.GetMyPortfolioAccountReportResponse>("ibcommissioncportalmanagement", "getMyPortfolioAccountReport", parameters)

    getMyPortfolioUnfundedAccountsReport = (parameters: IBTypes.GetMyPortfolioUnfundedAccountsReportParams) =>
        this._tfboCall<IBTypes.GetMyPortfolioUnfundedAccountsReportResponse>("ibcommissioncportalmanagement", "getMyPortfolioUnfundedAccountsReport", parameters)

    getMyPortfolioPendingReport = (parameters: IBTypes.GetMyPortfolioPendingReportParams) =>
        this._tfboCall<IBTypes.GetMyPortfolioPendingReportResponse>("ibcommissioncportalmanagement", "getMyPortfolioPendingReport", parameters)

    getMyPortfolioLeadsReport = (parameters: IBTypes.GetMyPortfolioLeadsReportParams) =>
        this._tfboCall<IBTypes.GetMyPortfolioLeadsReportResponse>("ibcommissioncportalmanagement", "getMyPortfolioLeadsReport", parameters)

    getMyPortfolioRebatesReport = (parameters: IBTypes.GetMyPortfolioRebatesReportBasicFilter) =>
        this._tfboCall<IBTypes.GetMyPortfolioRebatesReportResponse>("ibcommissioncportalmanagement", "getMyPortfolioRebatesReport", parameters)

    getMyPortfolioNetFundingReport = (parameters: IBTypes.GetMyPortfolioNetFundingReportParams) =>
        this._tfboCall<IBTypes.GetMyPortfolioNetFundingReportResponse>("ibcommissioncportalmanagement", "getMyPortfolioNetFundingReport", parameters)

    getMyPortfolioClientPaymentsReport = (parameters: IBTypes.GetMyPortfolioClientPaymentsReportParams) =>
        this._tfboCall<IBTypes.GetMyPortfolioClientPaymentsReportResponse>("ibcommissioncportalmanagement", "getMyPortfolioClientPaymentsReport", parameters)

    addPartnerReportNote = (parameters: IBTypes.AddPartnerReportNotesParams) =>
        this._tfboCall<{ statusMsg: string }>("ibcommissioncportalmanagement", "addPartnerReportNote", parameters)

    getPartnerReportNotes = (parameters: IBTypes.GetPartnerReportNotesParams) =>
        this._tfboCall<IBTypes.GetPartnerReportNotesResponse[]>("ibcommissioncportalmanagement", "getPartnerReportNotes", parameters)


    searchPartnerTradingAccountsSummaryPayload = (parameters: Types.SearchPartnerTradingAccountsSummaryParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement", action: "searchPartnerTradingAccountsSummary", parameters
    })

    searchPartnerTradingAccountsSummary = (parameters: Types.SearchPartnerTradingAccountsSummaryParams) =>
        this._tfboCall<IBTypes.AccountsSummary>("ibcommissioncportalmanagement", "searchPartnerTradingAccountsSummary", parameters)


    getAssetClassSymbols = () =>
        this._tfboCall<IBTypes.AssetClassSymbols[]>("ibcommissioncportalmanagement", "getAssetClassSymbols")

    getMasterIBSummary = (parameters: IBTypes.MasterIBSummaryParams) =>
        this._tfboCall<IBTypes.MasterIBSummaryResponse>("ibcommissioncportalmanagement", "getMasterIBSummary", parameters)

    getMasterIBSummaryPayload = (parameters: IBTypes.MasterIBSummaryParams): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement",
        action: "getMasterIBSummary",
        parameters: parameters
    })

    getMasterIBHierarchyReport = (parameters: IBTypes.MasterIBHierarchyReportRequest) =>
        this._tfboCall<IBTypes.MasterIBHierarchyReportResponse>("ibcommissioncportalmanagement", "getMasterIBHierarchyReport", parameters)

    getMasterIBHierarchyReportPayload = (parameters: IBTypes.MasterIBHierarchyReportRequest): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement",
        action: "getMasterIBHierarchyReport",
        parameters: parameters
    })

    getMasterIBHierarchyReportExcelDataPayload = (parameters: IBTypes.MasterIBHierarchyReportExcelDataRequest): Types.RequestBody => ({
        module: "ibcommissioncportalmanagement",
        action: "getMasterIBHierarchyReportExcelData",
        parameters: parameters
    })

    getMasterIBHierarchyReportExcelData = (parameters: IBTypes.MasterIBHierarchyReportExcelDataRequest) =>
        this._tfboCall<IBTypes.MasterIBHierarchyReportExcelDataResponse>("ibcommissioncportalmanagement", "getMasterIBHierarchyReportExcelData", parameters)

    downloadIBInvoice = <T>(invoiceID: string) =>
        this._download<T>(() => `${appConfig.IB_INVOICE_URL}?sid=${this.accessToken}&id=${invoiceID}`)

    setExchangeLoyaltyPoints = (parameters: Types.ExchangeLoyaltyPoints) =>
        this._tfboCall<Types.ExchangeLoyaltyPointsResult>("LoyaltyProgram", "exchangeLoyaltyPoints", parameters)

    ///Brokeree
    checkIfAccountIsAlreadyProvider = (userTradingAccountId: number) =>
        this._tfboCall<{
            accountIsAlreadyProvider: boolean
        }>("brokereecontroller", "checkIfAccountIsAlreadyProvider", { userTradingAccountId })

    checkIfAccountIsAlreadySubscribedToProvider = (userTradingAccountId: number) =>
        this._tfboCall<{
            accountIsAlreadySubscribedToProvider: boolean
        }>("brokereecontroller", "checkIfAccountIsAlreadySubscribedToProvider", { userTradingAccountId })

    registerNewProvider = (userTradingAccountId: number) =>
        this._tfboCall<{ status: string }>("brokereecontroller", "registerNewProvider", { userTradingAccountId })

    createNewBrokereeAccountAndClientIfNeeded = ({ userTradingAccountId, userId }:
        { userTradingAccountId: number, userId: number }) =>
        this._tfboCall<{
            registerNewClientStatus: boolean,
            registerNewAccountLinkedToTheGivenClientStatus: boolean
        }>("brokereecontroller", "createNewBrokereeAccountAndClientIfNeeded", { userTradingAccountId, userId })

    registerNewSubscription = ({ userTradingAccountId, providerId, offerId }:
        { userTradingAccountId: number, providerId: number, offerId: number }) =>
        //???????
        this._tfboCall<{ status: string }>("brokereecontroller", "registerNewSubscription", { userTradingAccountId, providerId, offerId })

    getAllActiveProviders = () =>
        this._tfboCall<{ items: Types.CopyProvider[] }>("brokereecontroller", "getAllActiveProviders", {})

    getRatingsForProviders = () =>
        this._tfboCall<{ items: Types.ProviderRating[] }>("brokereecontroller", "getRatingsForProviders", {})

    getAllOffersForProvider = (providerId: number) =>
        this._tfboCall<{ items: Types.ProviderOffer[] }>("brokereecontroller", "getAllOffersForProvider", { providerId })

    // THPRT-6273 / TMJV-968: ThinkCopy T&C acceptance (module thinkcopyterms), reached
    // through the standard nsdata envelope. The entity (TMSY/TMLC) is derived server-side
    // from the client's org, so the FE reads the active version + document at runtime and
    // records that exact version. Contract per the FE integration guide on TMJV-968.
    getLatestThinkCopyTermsAndConditions = () =>
        this._tfboCall<Types.ThinkCopyTermsVersion>("thinkcopyterms", "getLatestThinkCopyTermsAndConditions", {})

    recordThinkCopyTermsAcceptance = (params: Types.RecordThinkCopyTermsParams) =>
        this._tfboCall<void>("thinkcopyterms", "recordThinkCopyTermsAcceptance", { ...params })

    checkPAMMAccount = (userTradingAccountId: number) =>
        this._tfboCall<PAMMCheckResponse>("PammBrokereeController", "checkIfTradingAccountIsAlreadyMMorInvestor", { userTradingAccountId })

    registerInvestor = (
        data: {
            userTradingAccountId: number
            managerId?: number
            offerId?: number
            investment: number
            joinPassword?: string
            isDocumentSigned?: boolean
            document_version?: number
        }) =>
        this._tfboCall<RegisterInvestorResponse | PAMMError>("PammBrokereeController", "registerInvestor", data)

    registerMoneyManager = (
        data: {
            userTradingAccountId: number
            name: string
            description: string
            isPublicMM: boolean
            isDocumentSigned?: boolean
            document_version?: number
        }) =>
        this._tfboCall<{
            status: string
            data: RegisterMMResponse
        } | PAMMError>("PammBrokereeController", "registerMoneyManager", data)

    reinvestToSameManager = (
        data: {
            userTradingAccountId: number
            managerId: number
            amount: number
        }) =>
        this._tfboCall<ReinvestResponse | PAMMError>("PammBrokereeController", "reinvestToSameManager", data)

    withdrawFromManager = (
        data: {
            userTradingAccountId: number
            managerId: number
            amount: number
            comment?: string
        }) =>
        this._tfboCall<PAMMWithradResponse | PAMMError>("PammBrokereeController", "withdrawFromManager", data)

    checkAmountOfAvailableFunds = (
        data: {
            pammInvestorAccountId: number
            pammOwnerAccountId: number
        }) =>
        this._tfboCall<{
            body: {
                data: MMAvalableFunds
            }
        }>("PammBrokereeController", "checkAmountOfAvailableFunds", data)

    getAllOffersForMoneyManager = (managerId: number) =>
        this._tfboCall<{
            body: {
                data: PAMMManagerOffer[]
            }
        }>("PammBrokereeController", "getAllOffersForMoneyManager", { managerId })

    getManagerDetails = (managerId: number) =>
        this._tfboCall<{ data: MMDetails }>("PammBrokereeController", "getManagerDetails", { managerId })

    getApplicationCoolDownCheck = (applicationId: number) =>
        this._tfboCall<{ isSuccess?: boolean }>("application", "applicationCoolDownCheck", { applicationId })


    getSecondAttemptAppQuestions = (applicationId: number) =>
        this._tfboCall<Types.Question[]>("application", "getSecondAttemptAppQuestions", { applicationId })

    validateFilesExist = (files: string[]) =>
        this._tfboCall<{
            [index: string]: boolean
        }[]>("profile", "validateFilesExist", { files })

    getZotapayBranchedByBankCode = (bankCode: string) =>
        this._tfboCall<Types.ZotaBank[]>("ZotapayBankCportalController", "getZotapayBranchesByBankCode", { bankCode })

    getZotapayBanksByCountry = (countryId: number) =>
        this._tfboCall<Types.ZotaBank[]>("ZotapayBankCportalController", "getZotapayBanksByCountry", { countryId })

    getPrepaidCardSection = (signal?: AbortSignal) =>
        this._tfboCall<Types.PrepaidCardSectionResponse>("prepaidcard", "get_section", undefined, Authorize.Yes, signal)

    initiatePrepaidCardRequest = (data: Types.PrepaidCardInitiateRequestParams) =>
        this._tfboCall<Types.PrepaidCardRequestDraftResponse>("prepaidcard", "initiate_request", data)

    submitPrepaidCardRequest = (data: Types.PrepaidCardSubmitRequestParams) =>
        this._tfboCall<Types.PrepaidCardSectionResponse>("prepaidcard", "submit_request", data)

    getPrepaidCards = (signal?: AbortSignal) =>
        this._tfboCall<Types.PrepaidCardListResponse>("prepaidcard", "get_cards", undefined, Authorize.Yes, signal)

    prepaidCardDeposit = (data: Types.PrepaidCardDepositParams) =>
        this._tfboCall<Types.PrepaidCardDepositInitiatedResponse>("prepaidcard", "deposit", data)

    confirmPrepaidCardDeposit = (data: Types.PrepaidCardConfirmDepositParams) =>
        this._tfboCall<Types.PrepaidCardConfirmDepositResponse>("prepaidcard", "confirm_deposit", data)

    cancelPrepaidCardDeposit = (data: Types.PrepaidCardCancelDepositParams) =>
        this._tfboCall<Types.PrepaidCardConfirmDepositResponse>("prepaidcard", "cancel_deposit", data)

    prepaidCardWithdraw = (data: Types.PrepaidCardWithdrawParams) =>
        this._tfboCall<Types.PrepaidCardWithdrawalResponse>("prepaidcard", "withdraw", data)

    getPrepaidCardMovements = (data: Types.PrepaidCardGetMovementsParams, signal?: AbortSignal) =>
        this._tfboCall<Types.PrepaidCardMovementsResponse>("prepaidcard", "get_movements", data, Authorize.Yes, signal)
    private _competition = async <T>(
        path: string,
        options: {
            method?: string
            body?: object
            signal?: AbortSignal
            acceptStatus?: number[]
            authorize?: boolean
        } = {}
    ): Promise<{ status: number; body: T }> => {
        if (!appConfig.COMPETITION_API_URL)
            throw new Error('Weekend League is not configured for this environment')

        const base = appConfig.COMPETITION_API_URL.replace(/\/+$/, '')
        const { method = 'GET', body, signal, acceptStatus = [], authorize = true } = options
        const send = () => {
            // Anything beyond the CORS-safelisted headers turns a GET into a preflighted
            // request. The public routes answer a simple GET but not an OPTIONS preflight,
            // so a public read must send neither Authorization nor Content-Type.
            const headers: HeadersInit = {}
            if (body) headers['Content-Type'] = 'application/json'
            if (authorize && this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`
            return fetch(`${base}${path}`, {
                method,
                headers,
                body: body ? JSON.stringify(body) : undefined,
                signal,
            })
        }

        let response = await send()
        // A 401 here is not necessarily an expired session: the module answers 401
        // when the gateway has not injected its identity header. Retry once in case
        // the token really did expire, but never end the client's portal session over
        // a competition read.
        if (authorize && response.status === 401 && await this.refreshOnce()) {
            response = await send()
        }
        if (response.status !== 200 && acceptStatus.indexOf(response.status) < 0)
            throw new Error(`Weekend League request failed. HTTP status: ${response.status}`)

        return { status: response.status, body: await response.json() as T }
    }

    private _competitionOptional = async <T>(
        path: string, signal?: AbortSignal, authorize = true
    ): Promise<T | null> => {
        try {
            const { body } = await this._competition<T>(path, { signal, authorize })
            return body
        } catch {
            return null
        }
    }

    getWeekendLeagueReads = async (signal?: AbortSignal): Promise<WeekendLeague.WeekendLeagueResponses> => {
        const clientRead = <T>(segment: string) =>
            this._competitionOptional<T>(WeekendLeague.weekendLeagueClientPath(segment), signal)

        const language = getStore()?.getState?.()?.App?.language || appConfig.DEFAULT_LANGUAGE || 'en'

        const competition = await this._competition<WeekendLeague.CompetitionResponse>(
            `/competition?lang=${encodeURIComponent(language)}`, { signal, authorize: false })

        const competitionBody = competition.body
        if (!WeekendLeague.isWithinCompetitionWindow(competitionBody)) {
            return {
                competition: null,
                eligibleAccounts: null,
                registrations: null,
                position: null,
                nextRound: null,
                roundInProgress: false,
            }
        }

        const [eligibleAccounts, registrations] = await Promise.all([
            clientRead<WeekendLeague.EligibleAccountsResponse>('eligible-accounts'),
            clientRead<WeekendLeague.ClientRegistrationsResponse>('registrations'),
        ])

        const position = (registrations?.registrations || []).length
            ? await clientRead<WeekendLeague.PositionResponse>('position')
            : null

        // The countdown target. Both are public reads, so neither may send Authorization
        // or Content-Type, and both are best effort: without them the banner simply shows
        // no countdown. /weekends is the only route that reports which round is next, and
        // its standings is the only one that states the opening time as well as the date.
        const weekends = await this._competitionOptional<WeekendLeague.WeekendsResponse>(
            '/weekends', signal, false)
        const upcoming = WeekendLeague.selectNextRound(weekends)
        const roundStart = upcoming
            ? await this._competitionOptional<WeekendLeague.RoundStartResponse>(
                `/weekends/${upcoming.number}/standings`, signal, false)
            : null
        const nextRound = upcoming
            ? {
                roundNumber: upcoming.number,
                startsGmt: roundStart?.startsGmt || roundStart?.opensGmt || null,
            }
            : null
        const roundInProgress = WeekendLeague.isRoundInProgress(weekends)

        // THPRT-6408: COMPETITION_TERMS_VERSION
        if (appConfig.COMPETITION_TERMS_VERSION && !competitionBody?.terms?.version) {
            competitionBody.terms = {
                ...competitionBody.terms,
                version: appConfig.COMPETITION_TERMS_VERSION,
            }
        }

        return { competition: competitionBody, eligibleAccounts, registrations, position, nextRound, roundInProgress }
    }

    checkWeekendLeagueNickname = async (data: Types.WeekendLeagueNicknameCheckParams, signal?: AbortSignal) => {
        const { body } = await this._competition<WeekendLeague.NicknameAvailabilityResponse>(
            `/nicknames/availability?value=${encodeURIComponent(data.nickname)}`, { signal })

        return WeekendLeague.asPayload<Types.WeekendLeagueNicknameCheckResponse>({
            available: body?.available === true,
        })
    }

    registerForWeekendLeague = async (data: Types.WeekendLeagueRegisterParams) => {
        // The request is always sent, even when the terms version is missing and the
        // module is certain to refuse it. A refusal visible on the wire is worth more
        // than a tidy client-side shortcut while the contract is still being built.
        const { status, body } = await this._competition<WeekendLeague.RegisterResponse>(
            '/registrations',
            {
                method: 'POST',
                body: WeekendLeague.buildRegisterBody(data),
                acceptStatus: [400, 422],
            }
        )

        // 422 is the module's own terms or permanence gate. A 400 with no terms version
        // to send is the same cause reaching it as a schema failure instead, so it earns
        // the same wording rather than a generic failure.
        if (status === 422 || (status === 400 && !data.termsVersion))
            return WeekendLeague.asPayload(WeekendLeague.mapTermsRejection(data.entries))

        if (status === 400)
            throw new Error('Weekend League registration was rejected as invalid')

        return WeekendLeague.asPayload(WeekendLeague.mapRegisterResponse(body))
    }

    // THPRT-6468: rewards Kong gateway client. Sends the Keycloak access token as a
    // bearer, retries once on 401 in case the token had expired, and never sends an
    // x-api-key. Every rewards route is member-scoped, so every call is authorized and
    // the member is resolved from the token, not from any identifier in the request.
    // Returns the status so callers can treat a 404 as "not an enrolled member".
    private _rewards = async <T>(
        path: string,
        options: {
            signal?: AbortSignal
            acceptStatus?: number[]
        } = {}
    ): Promise<{ status: number; body: T }> => {
        const base = (appConfig.REWARDS_API_URL || '').replace(/\/+$/, '')
        const { signal, acceptStatus = [] } = options
        const send = () => {
            const headers: HeadersInit = {}
            if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`
            return fetch(`${base}/rewards/v1${path}`, { method: 'GET', headers, signal })
        }

        let response = await send()
        if (response.status === 401 && await this.refreshOnce()) {
            response = await send()
        }
        if (response.status !== 200 && acceptStatus.indexOf(response.status) < 0)
            throw new Error(`Rewards request failed. HTTP status: ${response.status}`)

        let body: T
        try {
            body = await response.json() as T
        } catch {
            if (response.status === 200)
                throw new Error('Rewards response was not valid JSON')
            body = {} as T
        }
        return { status: response.status, body }
    }

    rewardsGet = <T>(
        path: string,
        options?: { signal?: AbortSignal; acceptStatus?: number[] }
    ): Promise<{ status: number; body: T }> => this._rewards<T>(path, options)
}


const api = new Api()

export default api